import {logger} from '../../../logger';
import s from '../../s';
import {ProductDocSize} from '../../i';
import {toJS} from 'mobx';
import {allowsAutomatedDocSizes, docSizeCurrent, docSizeEnded, restoreFixedDisplaySizing} from './document-sizing';

export const Initialize = (): void => {
    restoreFixedDisplaySizing(s.prod, s.ps);
    s.docsizes_enabled = allowsAutomatedDocSizes(s.prod, s.si.AutomatedDocSizes);
    if (!s.docsizes_enabled) {
        TurnOff();
        return;
    }
    SetOriginalId();
    PulseChange(true);
    s.selected_doc_size = s.docsizes_orig_id;
    s.docsizes_selected_id = s.docsizes_orig_id;
};

export const PulseChange = (init: boolean) => {
    if (s.debug_lino) logger.log(`pulse-change: starting`);
    if (!s.docsizes_enabled || !allowsAutomatedDocSizes(s.prod, s.si.AutomatedDocSizes)) {
        s.docsizes_available = [];
        s.docsizes_multiple_active = false;
        if (s.debug_lino) logger.log(`pulse-change:  doc sizes not enabled, returning`);
        return;
    }

    var _matches = GetAvailableDocSizes();
    if (_matches.length === 0) {
        // The school has no sizes for this page (a store with automated doc sizes and no Enabled sizes): the product keeps its
        // own size and its own size choices, which both mount rules show once automated sizes are off.
        if (s.debug_lino) logger.log('pulse-change:  no doc sizes for this school, turning automated sizes off');
        TurnOff();
        SetPlexi();
        return;
    }

    const groups = new Map<string, number[]>();
    for (let i = 0; i < _matches.length; i++) {
        const name = _matches[i].DocSizeName;
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name)!.push(i);
    }
    for (const indices of groups.values()) {
        let earliestIdx = indices[0];
        let latestIdx = indices[0];
        const groupItems = indices.map(idx => _matches[idx]);
        const hasDiffYears = HasDifferentYears(groupItems);
        const hasDiffWidths = HasDifferentWidths(groupItems);
        const hasDiffHeights = HasDifferentHeights(groupItems);
        for (let j = 1; j < indices.length; j++) {
            const idx = indices[j];
            if (new Date(_matches[idx].StartDate).getTime() < new Date(_matches[earliestIdx].StartDate).getTime()) {
                earliestIdx = idx;
            }
            if (new Date(_matches[idx].EndDate).getTime() > new Date(_matches[latestIdx].EndDate).getTime()) {
                latestIdx = idx;
            }
        }
        for (const idx of indices) {
            _matches[idx].earliest_start_date = idx === earliestIdx;
            _matches[idx].latest_start_date = idx === latestIdx;
            _matches[idx].has_different_years = hasDiffYears;
            _matches[idx].has_different_widths = hasDiffWidths;
            _matches[idx].has_different_heights = hasDiffHeights;
        }
    }

    s.docsizes_has_different_names = HasDifferentNames(_matches);
    if (s.debug_lino) logger.log('pulse-change:  _matches', toJS(_matches));
    if (s.debug_lino) logger.log('pulse-change:  docsizes_has_different_names', s.docsizes_has_different_names);

    _matches.sort((a, b) => {
        const aIsBach = a.DocSizeName.toLowerCase().includes('bach') ? 0 : 1;
        const bIsBach = b.DocSizeName.toLowerCase().includes('bach') ? 0 : 1;
        if (aIsBach !== bIsBach) return aIsBach - bIsBach;

        const nameCompare = a.DocSizeName.localeCompare(b.DocSizeName);
        if (nameCompare !== 0) return nameCompare;

        const aStart = new Date(a.StartDate).getTime();
        const bStart = new Date(b.StartDate).getTime();
        if (aStart !== bStart) return bStart - aStart;

        const aEnd = new Date(a.EndDate).getTime();
        const bEnd = new Date(b.EndDate).getTime();
        if (aEnd !== bEnd) return bEnd - aEnd;

        return a.Doc_Width - b.Doc_Width;
    });

    if (_matches.length === 1) {
        s.docsizes_selected_id = _matches[0].Id;
        s.docsizes_name = _matches[0].DocSizeName;
    }

    if (init) {
        if (s.debug_lino) logger.log('pulse-change:  init, so setting original');
        SetOriginal();
    }
    s.docsizes_available = _matches;
    s.docsizes_multiple_active = _matches.length > 1;

    if (s.debug_lino) {
        logger.log('pulse-change:   s.docsizes_available', toJS(s.docsizes_available));
        logger.log('pulse-change:   s.docsizes_multiple_active', toJS(s.docsizes_multiple_active));
        logger.log(`pulse-change: completed`);
    }

    SetPlexi();
};

// A document over 11 x 14 (either way) gets acrylic, as does a product that forces it.
const SetPlexi = (): void => {
    s.ps.use_plexi = s.prod.ForcePlexiglass || (s.ps.docWidth > 11 && s.ps.docHeight > 14) || (s.ps.docWidth > 14 && s.ps.docHeight > 11);
};

// Automated sizes do not apply to the page: the product keeps its own size and size choices.
const TurnOff = (): void => {
    s.docsizes_enabled = false;
    s.docsizes_available = [];
    s.docsizes_multiple_active = false;
    s.docsizes_orig_id = -1;
    s.docsizes_selected_id = -1;
    s.ps.DocSizeID = 0;
};

const GetAvailableDocSizes = (): ProductDocSize[] => {
    // return if specific grad school matches
    if (s.currentGradSchoolID > 0) {
        // return gs specific ids (assume if specific gs assignments on doc sizes - then all relevant doc sizes for that gs have specific assignments)
        let gs_matches = s.si.ProductDocSizes.filter(x => x.GradSchoolID === s.currentGradSchoolID);
        if (gs_matches && gs_matches.length > 0) return gs_matches;
    }

    // return -1 ids - matches non-gs and any gs that don't have specific sizes assigned
    let matches = s.si.ProductDocSizes.filter(x => x.GradSchoolID < 1);
    if (matches && matches.length > 0) return matches;

    return []; // return no matches if no matches
};

const HasDifferentYears = (_matches: ProductDocSize[]): boolean => {
    if (!_matches || _matches.length < 2) return false;

    const firstStart = new Date(_matches[0].StartDate).getTime();
    const firstEnd = new Date(_matches[0].EndDate).getTime();

    for (let i = 1; i < _matches.length; i++) {
        if (new Date(_matches[i].StartDate).getTime() !== firstStart || new Date(_matches[i].EndDate).getTime() !== firstEnd) {
            return true;
        }
    }

    return false;
};

const HasDifferentNames = (_matches: ProductDocSize[]): boolean => {
    if (!_matches || _matches.length < 2) return false;

    const firstName = _matches[0].DocSizeName;

    for (let i = 1; i < _matches.length; i++) {
        if (_matches[i].DocSizeName !== firstName) {
            return true;
        }
    }

    return false;
};

const HasDifferentWidths = (_matches: ProductDocSize[]): boolean => {
    if (!_matches || _matches.length < 2) return false;

    const firstWidth = _matches[0].Doc_Width;

    for (let i = 1; i < _matches.length; i++) {
        if (_matches[i].Doc_Width !== firstWidth) {
            return true;
        }
    }

    return false;
};

const HasDifferentHeights = (_matches: ProductDocSize[]): boolean => {
    if (!_matches || _matches.length < 2) return false;

    const firstHeight = _matches[0].Doc_Height;

    for (let i = 1; i < _matches.length; i++) {
        if (_matches[i].Doc_Height !== firstHeight) {
            return true;
        }
    }

    return false;
};

export const SetOriginalId = (): number => {
    let _bestMatch: ProductDocSize | null = null;

    const now = new Date();

    if (s.currentGradSchoolID > 0) {
        _bestMatch = s.si.ProductDocSizes.find(x => x.GradSchoolID === s.currentGradSchoolID && x.Doc_Width === s.prod.docWidth && x.Doc_Height === s.prod.docHeight && docSizeCurrent(x, now)) ?? null;
        if (!_bestMatch) {
            _bestMatch = s.si.ProductDocSizes.find(x => x.GradSchoolID === s.currentGradSchoolID && docSizeCurrent(x, now)) ?? null;
            if (s.debug_lino) logger.log(`SetOriginalId:  3`);
        }
        if (!_bestMatch && !s.si.ProductDocSizes.some(x => x.GradSchoolID === s.currentGradSchoolID) && s.ui.categoryIds.length === 0) {
            // A grad school without sizes of its own is offered the school's (GetAvailableDocSizes), so its product starts at its own
            // size when that is one of them. A product built at a size the school does not list (a law degree), or one whose category
            // tiles offer the other sizes, keeps sizes off and its own size.
            _bestMatch = s.si.ProductDocSizes.find(x => x.GradSchoolID < 1 && x.Doc_Width === s.prod.docWidth && x.Doc_Height === s.prod.docHeight && docSizeCurrent(x, now)) ?? null;
        }
    } else {
        _bestMatch = s.si.ProductDocSizes.find(x => x.GradSchoolID < 1 && x.Doc_Width === s.prod.docWidth && x.Doc_Height === s.prod.docHeight && docSizeCurrent(x, now)) ?? null;
        if (!_bestMatch) {
            _bestMatch = s.si.ProductDocSizes.find(x => x.GradSchoolID < 1 && docSizeCurrent(x, now)) ?? null;
            if (s.debug_lino) logger.log(`SetOriginalId:  4`);
        }
    }

    if (s.debug_lino && _bestMatch) logger.log(`SetOriginalId:  SetOriginal: best match, ${_bestMatch.Id}, w:${_bestMatch.Doc_Width}, h:${_bestMatch.Doc_Height}`);

    s.docsizes_orig_id = _bestMatch ? _bestMatch.Id : -1;

    return s.docsizes_orig_id;
};

const SetOriginal = () => {
    var _id = SetOriginalId();

    s.ui.update_pricing = true;
    s.ui.doRender = true;
    if (_id < 1) {
        if (s.debug_lino) logger.log('SetOriginal:  SetOriginal: no best match, setting docsizes_enabled to false');
        s.docsizes_enabled = false;
    } else {
        SetDocSize(_id);
    }
};

export const SetDocSize = (_id: number): boolean => {
    if (!s.docsizes_enabled || !allowsAutomatedDocSizes(s.prod, s.si.AutomatedDocSizes)) return false;
    var _match = s.si.ProductDocSizes.find(x => x.Id === _id) ?? null;
    if (!_match) {
        logger.error('doc-size-pulse.ts:SetDocSize: no doc size matched');
        return false;
    }

    s.ps.docWidth = _match.Doc_Width;
    s.ps.docHeight = _match.Doc_Height;
    s.ps.DocSizeID = _match.Id;
    s.docsizes_selected_id = _match.Id;
    s.selected_doc_size = _match.Id;
    s.docsizes_name = GetDocSizeName(_match);
    SetPlexi();

    return true;
};

export const GetDocSizeName = (_doc: ProductDocSize): string => {
    const nameCount = s.docsizes_available.filter(x => x.DocSizeName === _doc.DocSizeName).length;
    if (nameCount <= 1) return _doc.DocSizeName;
    let used_doc_size_name = false;

    if (_doc.has_different_years) {
        const start_date = FormatMonthYear(_doc.StartDate);
        const end_date = FormatMonthYear(_doc.EndDate);
        let date_str = ``;

        if (_doc.earliest_start_date) {
            // EndDate is the size's last month, so the oldest size covers the documents before the month after it.
            date_str = `Pre-${FormatMonthYear(MonthAfter(_doc.EndDate))}`;
        } else if (_doc.latest_start_date || !docSizeEnded(_doc, new Date())) {
            date_str = `${start_date} - present`;
        } else {
            date_str = `${start_date} - ${end_date}`;
        }

        // if(_doc.has_different_widths || _doc.has_different_heights){
        //     date_str = `${date_str} ${_doc.DocSizeName} ${_doc.Doc_Width}"w x ${_doc.Doc_Height}"h`;
        //     used_doc_size_name = true;
        // }

        if (s.docsizes_has_different_names && !used_doc_size_name) {
            return _doc.DocSizeName.includes('Bachelors/Masters') ? date_str : `${_doc.DocSizeName} ${date_str}`;
        } else {
            return `${date_str}`;
        }
    } else if (!_doc.has_different_years && (_doc.has_different_widths || _doc.has_different_heights)) {
        return `${_doc.DocSizeName} ${_doc.Doc_Width}"w x ${_doc.Doc_Height}"h`;
    } else {
        return _doc.DocSizeName;
    }
};

const MonthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MonthAfter = (_date: Date | string): Date => {
    const date = new Date(_date);
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
};

const FormatMonthYear = (_date: Date | string): string => {
    const dateFixed = typeof _date === 'string' ? new Date(_date) : _date;
    return `${MonthAbbr[dateFixed.getMonth()]} ${dateFixed.getFullYear()}`;
};
